/**
 * @file Stages Context Definition
 * @description Defines the React context for accessing plant growth stages.
 * 
 * This file provides the TypeScript interface and context definition for
 * the stages context, which allows components to access information about
 * plant growth stages throughout the application.
 * 
 * @module context/StagesContextDefinition
 */

import { createContext } from 'react';
import { Stage, StageId } from '../types/stage';

/**
 * StagesContextType interface
 * @interface StagesContextType
 * @description Defines the shape of the stages context value
 * 
 * This interface specifies all properties and methods that will be available
 * through the stages context, including access to stage data and utility
 * functions for retrieving and transforming stage information.
 */
export interface StagesContextType {
  /** Array of all available plant growth stages */
  stages: Stage[] | undefined;
  
  /** Flag indicating if stages are currently being loaded */
  isLoading: boolean;
  
  /** Error object if there was a problem loading stages */
  error: Error | null;
  
  /** Function to trigger a refresh of stage data */
  refetchStages: () => Promise<unknown>;
  
  /**
   * Get a specific stage by its ID
   * @param {StageId} stageId - The ID of the stage to retrieve
   * @returns {Stage | null} The stage object if found, null otherwise
   */
  getStageById: (stageId: StageId) => Stage | null;
}

/**
 * StagesContext
 * @constant StagesContext
 * @description React context for accessing plant growth stage data
 * 
 * This context provides access to plant growth stages throughout the application,
 * along with utility functions for working with stage data.
 * 
 * @type {React.Context<StagesContextType | undefined>}
 */
export const StagesContext = createContext<StagesContextType | undefined>(undefined);